import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Row, Col, Spinner, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { RequiredLabel } from '../../utils/validators';
import { getRoles } from '../../services/Other/GeneralQueries';
import { addUser } from '../../services/Users/AddUser';
import { updateUser, getUserToEdit } from '../../services/Users/EditUser';

const DEFAULT_PASSWORD = '1nV3nt@rio2025';

export default function UserModal({ show, mode, userId, onHide, onSaved }) {
    const isEdit = mode === 'edit';

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [roles, setRoles] = useState([]);

    const [formData, setFormData] = useState({
        email: '',
        username: '',
        roleId: '0',
    });

    const requiredMissing = useMemo(() => {
        const emailOk = String(formData.email || '').trim().length > 0;
        const nameOk = String(formData.username || '').trim().length > 0;
        const roleOk = Number(formData.roleId) > 0;
        return !(emailOk && nameOk && roleOk);
    }, [formData]);

    const close = () => {
        if (saving) return;
        onHide?.();
    };

    useEffect(() => {
        if (!show) return;

        let cancelled = false;

        (async () => {
            setLoading(true);
            try {
                const rolesList = await getRoles();
                if (!cancelled) setRoles(Array.isArray(rolesList) ? rolesList : []);

                if (isEdit && userId) {
                    const usr = await getUserToEdit(userId);
                    if (!cancelled) {
                        setFormData({
                            email: usr?.email || '',
                            username: usr?.userName || '',
                            roleId: String(usr?.roleId || 0),
                        });
                    }
                } else {
                    // modo add: limpia
                    if (!cancelled) {
                        setFormData({ email: '', username: '', roleId: '0' });
                    }
                }
            } catch (e) {
                console.error(e);
                toast.error(isEdit ? 'No se pudo cargar el usuario.' : 'No se pudo cargar la configuración.');
                onHide?.();
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [show, isEdit, userId, onHide]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((p) => ({ ...p, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (requiredMissing) {
            toast.warning('Completa Email, Nombre y Rol.');
            return;
        }

        setSaving(true);
        try {
            const email = String(formData.email).trim();
            const userName = String(formData.username).trim();
            const roleId = Number(formData.roleId);

            if (isEdit) {
                const ok = await updateUser(userId, email, userName, roleId);
                if (!ok) {
                    toast.error('Lo sentimos, algo salió mal.');
                    return;
                }
                toast.success('Usuario actualizado correctamente');
            } else {
                const ok = await addUser(email, userName, roleId);
                if (!ok) {
                    toast.error('Lo sentimos, algo salió mal.');
                    return;
                }
                toast.success('Usuario creado correctamente');
            }

            onSaved?.(); // refresca tabla + cierra modal desde el padre
        } catch (e) {
            console.error(e);
            toast.error(isEdit ? 'No se pudo actualizar el usuario.' : 'No se pudo crear el usuario.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={close} centered size="lg">
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton={!saving}>
                    <Modal.Title>{isEdit ? 'Editar usuario' : 'Agregar usuario'}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Cargando...
                        </div>
                    ) : (
                        <>
                            {!isEdit && (
                                <div className="mb-3">
                                    Contraseña por defecto:{' '}
                                    <Badge bg="secondary">{DEFAULT_PASSWORD}</Badge>
                                </div>
                            )}

                            <Row className="g-3">
                                <Col md={6}>
                                    <Form.Label as={RequiredLabel} required>Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                </Col>

                                <Col md={6}>
                                    <Form.Label as={RequiredLabel} required>Nombre de usuario</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="username"
                                        value={formData.username}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                </Col>

                                <Col md={12}>
                                    <Form.Label as={RequiredLabel} required>Rol</Form.Label>
                                    <Form.Select
                                        name="roleId"
                                        value={formData.roleId}
                                        onChange={handleChange}
                                        disabled={saving}
                                    >
                                        <option value="0">Seleccionar rol</option>
                                        {roles.map((r) => (
                                            <option key={r.id} value={r.id}>
                                                {r.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>
                            </Row>
                        </>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" type="button" onClick={close} disabled={saving}>
                        Cancelar
                    </Button>
                    <Button variant="primary" type="submit" disabled={saving || loading || requiredMissing}>
                        {saving ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Guardando...
                            </>
                        ) : (
                            'Guardar'
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}